import {
  BlockDataType,
  BlockType,
  EDITOR_MODE,
  useBlocksConfig,
  useEditorStateSelector,
  useViewerState
} from "@pathwright/blocks-engine"
import { useBlocksLinks } from "../blocksLinks/functions"
import ProgressBar from "@pathwright/ui/src/components/progress/ProgressBar"
import { LazyQuillEditor } from "@pathwright/ui/src/components/quill/LazyQuillEditor"
import { usePreload } from "@pathwright/ui/src/components/utils/preload"
import { useCallback } from "react"
import styled from "styled-components"
import ErrorOverlay from "../ui/ErrorOverlay"
import { DefaultLoadingWithTimeout } from "../utils/LoadingWithTimeout"
import BlockViewer from "./BlockViewer"
import EmptyContent from "./EmptyContent"

const Container = styled.div`
  position: relative;
`
type BlockViewerContainerProps = {
  block: BlockType
  blockIndex: number
  onBlockChange?: (data: BlockDataType, id: string) => void
}

function BlockViewerContainer({
  block,
  blockIndex,
  onBlockChange
}: BlockViewerContainerProps) {
  const editorMode = useEditorStateSelector((state) => state.mode)
  const { saveBlock } = useViewerState()

  const handleChange = useCallback(
    (id: string, data: BlockDataType, trackProgress?: boolean) => {
      // Optimistic user scoped update for Editor Preview mode
      if (onBlockChange && editorMode === EDITOR_MODE.EDIT) {
        onBlockChange(data, id)
      }
      saveBlock(id, data, trackProgress)
    },
    []
  )

  return (
    <BlockViewer
      block={block}
      blockIndex={blockIndex}
      onChange={handleChange}
    />
  )
}

type BlocksViewerProps = {
  onBlockChange?: (data: BlockDataType, id: string) => void
}

function BlocksViewer({ onBlockChange }: BlocksViewerProps) {
  const { blocks } = useViewerState()
  const { userID, mode: blocksMode } = useBlocksConfig()

  useBlocksLinks(blocksMode ?? "VIEW")

  return blocks.map((block, i) => {
    return (
      <BlockViewerContainer
        key={`block-viewer-${block.contentID}:${block.id}-${userID}`}
        block={block}
        blockIndex={i}
        onBlockChange={onBlockChange}
      />
    )
  })
}

type ContentViewerProps = {
  blocks?: BlockType[]
  onBlockChange?: (data: BlockDataType, id: string) => void
  children?: React.ReactNode
}

export const ContentViewer = ({
  blocks: propsBlocks,
  onBlockChange,
  children
}: ContentViewerProps) => {
  const {
    blocks: viewerBlocks,
    completion,
    error,
    contentLoading
  } = useViewerState()
  const { contextKey } = useBlocksConfig()
  const blocks = propsBlocks || viewerBlocks

  // HACK: showing loader until QuillEditor is loaded for a more
  // seemless transition from loading to displaying content
  const [preloading] = usePreload({
    Component: LazyQuillEditor,
    preload: true
  })

  if ((preloading && process.env.NODE_ENV !== "test") || contentLoading) {
    return <DefaultLoadingWithTimeout />
  }

  if (error) {
    return (
      <div key={"content-viewer"}>
        <Container>
          {error ? <ErrorOverlay error={error?.message || ""} /> : null}
          {children ?? null}
        </Container>
      </div>
    )
  }

  return (
    <div key={"content-viewer"} id={`blocks-content-viewer-${contextKey}`}>
      {children}
      <ProgressBar
        progress={completion.progress}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10
        }}
      />
      {!blocks.length ? (
        <EmptyContent />
      ) : (
        <BlocksViewer onBlockChange={onBlockChange} />
      )}
    </div>
  )
}
